import { Insurer, PracticeProfile, SpecialistProfile } from '../../types'
import useTranslateMessage from '../../hooks/useTranslateMessage'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { BookingSlot } from '../../types/booking'
import { useSpecialistPageConfig } from '../../hooks/useSpecialistPageConfig'
import { getSpecialistSlotsBySlug } from '../../api/specialist'
import {
  convertBookingSlotRawToSlotType,
  transformSpecialistBookingSlots
} from '../../utils/transformSpecialistBookingSlots'
import { BookingCalendarProvider, useBookingCalendar } from '../../providers/BookingCalendar'
import BookingCalendarModal from '../BookingCalendar/BookingCalendarModal'
import dynamic from 'next/dynamic'
import { Button, styledComponent, useTheme } from '@hermes/web-components'
import NextAvailableSlotLabel from '../profiles/ProfileModal/NextAvailableSlotLabel'
import { calendarUtils } from '@hermes/booking-calendar'
import { SearchProfilesPageContext } from '../../providers/SearchProfilesPage'
import { getBookingInsurers } from '../../api/search'
import { TEST_IDS } from '../../__tests__/testUtils/constants'

const { formatDateStringToDisplay, formatTimeStringToDisplay } = calendarUtils

const BookAppointmentModal = dynamic(() => import('../profiles/ProfileModal/BookAppointmentModal'), {
  ssr: false
})

const ContactButton = styledComponent(Button)(({ theme }) => ({
  ...(theme?.custom?.SearchProfileCard?.contactButton ?? {})
}))

const BookAppointmentModalWithBookingContext = ({
  toggleContactsModal,
  specialist,
  practice
}: {
  toggleContactsModal: () => void
  specialist?: SpecialistProfile
  practice?: PracticeProfile
}) => {
  const context = useBookingCalendar()

  return (
    <BookAppointmentModal
      isOpen
      onClose={toggleContactsModal}
      specialist={specialist}
      practice={practice}
      bookingCalendarContext={context}
      bookingModalActiveButton="all"
    />
  )
}

const SearchAppointmentModal = ({
  specialist,
  practice,
  isUnclaimed,
  totalReviews,
  profileFullName,
  isSearchPage,
  externalId,
  isPractice
}: {
  specialist?: SpecialistProfile
  practice?: PracticeProfile
  isUnclaimed: boolean
  totalReviews: number
  profileFullName: string
  externalId: string
  isPractice: boolean
  isSearchPage?: boolean
}) => {
  const {
    state: { clientReloaded }
  } = useContext(SearchProfilesPageContext)
  const specialistPracticeWithSlots = specialist?.practices.find((practice) => practice?.slots?.length > 0)

  const practiceBookingSlots = specialistPracticeWithSlots?.slots.map((slot) => ({
    ...convertBookingSlotRawToSlotType(slot),
    practice: specialistPracticeWithSlots
  }))

  const theme = useTheme()
  const translate = useTranslateMessage()
  const [showContactsModal, setShowContactsModal] = useState(false)
  const { locale } = useRouter()
  const [bookingSlots, setBookingSlots] = useState<BookingSlot[]>(practiceBookingSlots ?? [])
  const [bookingInsurances, setBookingInsurances] = useState<Array<Insurer>>([])

  const toggleContactsModal = useCallback(() => setShowContactsModal((prev: boolean) => !prev), [])

  const onClickContact = useCallback(() => {
    toggleContactsModal()
    window.dataLayer.push({
      event: 'prf_pg_cont_button',
      cont_prf_name: profileFullName,
      cont_prf_type: isPractice ? 'Practice' : 'Specialist',
      cont_interaction_label: 'Contact',
      cont_prf_uuid: externalId
    })
  }, [externalId, isPractice, profileFullName, toggleContactsModal])

  if (!specialist && !practice) {
    return null
  }

  if (isPractice) {
    return (
      <>
        <ContactButton
          data-testid={TEST_IDS.SEARCH_RESULT_CARD_CONTACT_BUTTON}
          variant="contained"
          fullWidth
          onClick={onClickContact}
          sx={{ mt: isSearchPage ? 0 : 1, color: 'common.white', borderRadius: '24px' }}
        >
          {translate('footer.contact')}
        </ContactButton>
        {showContactsModal && (
          <BookAppointmentModal
            isOpen
            onClose={toggleContactsModal}
            specialist={specialist}
            practice={practice}
            bookingModalActiveButton="all"
          />
        )}
      </>
    )
  }

  const {
    bookingCalendarSpecialistProfile,
    bookingCalendarSpecialistPractices,
    bookingCalendarSlots,
    bookingCalendarVisitReasons
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useSpecialistPageConfig({
    specialist: specialist!,
    isUnclaimed,
    bookingSlots,
    totalReviews
  })

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (showContactsModal && specialist?.slug) {
      getSpecialistSlotsBySlug({ lang: String(locale), slug: specialist.slug }).then((slots) => {
        if ('availability' in slots) {
          setBookingSlots(transformSpecialistBookingSlots(slots))
          getBookingInsurers({ locale: String(locale) }).then((insurers) => setBookingInsurances(insurers))
        }
      })
    }
  }, [locale, showContactsModal, specialist?.slug])

  const practiceWithSlots = bookingCalendarSlots.find((slot) => slot.slots.length > 0)
  const firstPracticeSlot = practiceWithSlots?.slots[0]
  const bookingIsAvailable = !!firstPracticeSlot
  const firstAvailableBooking = `${formatDateStringToDisplay(firstPracticeSlot?.date)} • ${formatTimeStringToDisplay(
    firstPracticeSlot?.time
  )}`

  return (
    <>
      {bookingIsAvailable && (
        <NextAvailableSlotLabel
          withoutDateTime={!clientReloaded}
          sx={{
            mt: '16px',
            [theme.breakpoints.down('sm')]: {
              mt: '-4px',
              mb: '12px'
            }
          }}
        >
          {firstAvailableBooking}
        </NextAvailableSlotLabel>
      )}
      <ContactButton
        data-testid={TEST_IDS.SEARCH_RESULT_CARD_CONTACT_BUTTON}
        variant="contained"
        fullWidth
        onClick={onClickContact}
        sx={{ mt: isSearchPage ? 0 : 1, color: 'common.white', borderRadius: '24px' }}
      >
        {translate('footer.contact')}
      </ContactButton>
      <BookingCalendarProvider
        key={specialist?.id}
        specialistProfile={bookingCalendarSpecialistProfile}
        toggleBookingModal={toggleContactsModal}
        practices={bookingCalendarSpecialistPractices}
        slots={bookingCalendarSlots}
        visitReasons={bookingCalendarVisitReasons}
        insurances={bookingInsurances}
      >
        {showContactsModal && (
          <BookAppointmentModalWithBookingContext
            toggleContactsModal={toggleContactsModal}
            specialist={specialist}
            practice={practice}
          />
        )}
        <BookingCalendarModal />
      </BookingCalendarProvider>
    </>
  )
}

export default SearchAppointmentModal
